import type { AnimationPlaybackState } from "../../../shared/api-contract";
import { Slider } from "./studioControls";

/** Local MiniButton — ported from src/ui/ring/controls.tsx (which is being deleted). */
function MiniButton({ children, onClick, active }: { children: React.ReactNode; onClick: () => void; active?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
        active
          ? "border-ed-text bg-ed-text text-white"
          : "border-ed-line bg-ed-panel text-ed-text hover:border-ed-accent"
      }`}
    >
      {children}
    </button>
  );
}

interface Props {
  state: AnimationPlaybackState;
  disabled?: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (t: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
  onToggleLoop: (v: boolean) => void;
  onRate: (v: number) => void;
}

export function TimelineTransport({
  state, disabled, onPlay, onPause, onStop, onSeek, onScrubStart, onScrubEnd, onToggleLoop, onRate,
}: Props) {
  const dur = Math.max(0.01, state.duration);
  return (
    <div className={`flex flex-col gap-2 rounded-lg bg-ed-panel-2 px-2 py-2 ${disabled ? "pointer-events-none opacity-50" : ""}`}>
      <div className="flex items-center gap-1.5">
        {state.playing ? (
          <MiniButton active onClick={onPause}>Pause</MiniButton>
        ) : (
          <MiniButton onClick={onPlay}>Play</MiniButton>
        )}
        <MiniButton onClick={onStop}>Stop</MiniButton>
        <MiniButton active={state.loop} onClick={() => onToggleLoop(!state.loop)}>Loop</MiniButton>
        <span className="ml-auto text-[12px] tabular-nums text-ed-text-2">
          {state.currentTime.toFixed(2)}s / {dur.toFixed(2)}s
        </span>
      </div>
      <Slider
        label="Scrub"
        min={0}
        max={dur}
        step={0.01}
        value={Math.min(state.currentTime, dur)}
        onDragStart={onScrubStart}
        onDragEnd={onScrubEnd}
        onChange={onSeek}
      />
      <Slider label="Speed" min={0.25} max={3} step={0.05} value={state.playbackRate} unit="×" onChange={onRate} />
    </div>
  );
}
